/**
 * The estate as a tree, and the questions the pickers ask of it.
 *
 * Pure by design — no Vue, no store, no network. Every consumer loads the whole active tree
 * once and asks these functions about it, which is what makes ancestor context free: a path
 * is a walk over an index already in hand rather than another read.
 *
 * Nothing prevents a Location from being its own ancestor. Aidbox does not reject a partOf
 * cycle, so every walk here carries a visited set. Without one a cycle is not a wrong answer,
 * it is a hung browser tab.
 */

/** Purposes are ours. Written on Location.type by the node dialog and the backfill. */
export const PURPOSE_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/location-purpose';

/** Shapes are HL7's. Location.physicalType, binding strength example. */
export const SHAPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/location-physical-type';

/**
 * What a place may be used for — the axis every consumer filters on.
 *
 * Hardcoded rather than read from the terminology server because code branches on these values:
 * a runtime lookup for something a switch depends on buys nothing and can fail. The same codes
 * ship in fhir-package for validation and in a terminology migration for reporting; adding one
 * means all three.
 */
export const LOCATION_PURPOSES = [
    { value: 'book', title: 'Bookable', icon: 'calendar-check', color: 'info', description: 'Appointments can be scheduled into here.' },
    { value: 'medAdmin', title: 'Medication administration', icon: 'pills', color: 'pending', description: 'Medications are given from here, and a medication order can name it.' },
    { value: 'occupancy', title: 'Patient occupancy', icon: 'bed', color: 'related', description: 'A patient can be assigned here. Beds can be added beneath it.' },
    // Not offered when adding a place: a claim's service location is the location itself, set on
    // the location editor, and every root carries this from the moment it is created. It stays in
    // the vocabulary so a root still reads as one.
    { value: 'serviceSite', title: 'Service site', icon: 'file-invoice-dollar', color: 'organization', rootOnly: true, description: 'Stands for a service location on a claim.' },
];

/**
 * What shape a place is — HL7's own list, offered minus the codes that describe public health
 * rather than an estate (rd Road, area Area, jdn Jurisdiction).
 *
 * Never a filter. A caller asks for a purpose and gets back whatever shapes carry it; the shape
 * is what the answer is labeled and iconed with.
 */
export const LOCATION_SHAPES = [
    { value: 'si', title: 'Site', icon: 'location-dot', description: 'A collection of buildings or other locations such as a site or a campus.' },
    { value: 'bu', title: 'Building', icon: 'building', description: 'Any building or structure, which may contain rooms, corridors and wings.' },
    { value: 'ho', title: 'House', icon: 'house', description: 'A residential dwelling a person may reside in.' },
    { value: 'wi', title: 'Wing', icon: 'diagram-project', description: 'A wing within a building, often containing levels, rooms and corridors.' },
    { value: 'lvl', title: 'Level', icon: 'layer-group', description: 'A level in a multi-level building.' },
    { value: 'co', title: 'Corridor', icon: 'grip-lines', description: 'A corridor within a building, connecting rooms.' },
    { value: 'wa', title: 'Ward', icon: 'hospital', description: 'A section of a medical facility that may contain rooms and other locations.' },
    { value: 'ro', title: 'Room', icon: 'door-open', description: 'A space allocated as a room.' },
    { value: 'bd', title: 'Bed', icon: 'bed', description: 'A space allocated for sleeping or laying on — the space, not the furniture.' },
    { value: 'ca', title: 'Cabinet', icon: 'cart-flatbed', description: 'A container storing goods, equipment or medications. A med cart.' },
    { value: 've', title: 'Vehicle', icon: 'van-shuttle', description: 'A means of transportation.' },
];

/** The only shape a root may be, and the only shape nothing below a root may be. */
export const ROOT_SHAPE = 'si';

/** A bed is definitionally occupancy, so it carries no purpose and goes nowhere else. */
export const BED_SHAPE = 'bd';
const BED_REQUIRES_PURPOSE = 'occupancy';

/**
 * What each kind of place may contain.
 *
 * Read from HL7's own definitions rather than invented: a wing is "within a building", a level is
 * "in a multi-level building", a corridor "within a building, that may connect rooms". So the map
 * is mostly those sentences turned around — a building is what wings and levels are offered under.
 *
 * The root is the exception: a Site may hold any kind of place at all. It is the one level every
 * account has, and an agency that keeps its whole estate one level down should not have to model a
 * building it does not think in terms of.
 *
 * One other call the definitions do not settle: a Ward may hold a Bed directly, since a ward of
 * beds with no rooms between them is ordinary.
 *
 * A shape absent from this map, or mapped to nothing, is a leaf: a bed contains nothing, and
 * neither does a cart.
 */
const ALLOWED_CHILDREN = {
    si: ['bu', 'ho', 'wi', 'lvl', 'co', 'wa', 'ro', 'bd', 'ca', 've'],
    bu: ['wi', 'lvl', 'co', 'wa', 'ro'],
    ho: ['wi', 'lvl', 'co', 'wa', 'ro'],
    wi: ['lvl', 'co', 'wa', 'ro'],
    lvl: ['co', 'wa', 'ro'],
    co: ['ro', 'ca'],
    wa: ['ro', 'bd', 'ca'],
    ro: ['bd', 'ca'],
    bd: [],
    ca: [],
    ve: ['ca'],
};

/**
 * Index the estate for the walks below.
 *
 * @param {Array<object>} locations
 * @returns {{byId: Map<string, object>, childrenOf: Map<string, Array<object>>, roots: Array<object>}}
 */
export const buildIndex = (locations) => {
    const byId = new Map();
    (locations || []).forEach((location) => {
        if (location?.id) byId.set(location.id, location);
    });

    const childrenOf = new Map();
    const roots = [];

    byId.forEach((location) => {
        const parentId = location.partOf?.id;
        // A parent outside the loaded set leaves this a root of what we can see. Rendering it
        // where it sits beats dropping it — an unreachable location cannot be fixed.
        const detached = !parentId || !byId.has(parentId);
        // Every node of a cycle is unreachable from any root and would vanish from the tree
        // entirely. Promoting them puts them back on screen, where they can be re-parented.
        if (detached || climbsIntoACycle(location, byId)) {
            roots.push(location);
            return;
        }
        if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
        childrenOf.get(parentId).push(location);
    });

    return { byId, childrenOf, roots };
};

const climbsIntoACycle = (location, byId) => {
    const seen = new Set();
    let current = location;
    while (current) {
        if (seen.has(current.id)) return true;
        seen.add(current.id);
        current = byId.get(current.partOf?.id);
    }
    return false;
};

/**
 * The purposes a place carries.
 *
 * A coding from another system is somebody else's vocabulary and is ignored rather than
 * guessed at; a coding with no system at all is read, because fixtures and hand-written
 * seed data leave it off.
 *
 * @param {object} location
 * @returns {Array<string>}
 */
export const purposesOf = (location) =>
    (location?.type || [])
        .flatMap((concept) => concept?.coding || [])
        .filter((coding) => !coding?.system || coding.system === PURPOSE_SYSTEM)
        .map((coding) => coding?.code)
        .filter(Boolean);

/**
 * Whether a place carries a purpose.
 *
 * @param {object} location
 * @param {string} purpose a code from LOCATION_PURPOSES
 * @returns {boolean}
 */
export const hasPurpose = (location, purpose) => purposesOf(location).includes(purpose);

/**
 * What shape a place is. Existing rooms carry a bare `ro` with no system, so the system is not
 * required here.
 *
 * @param {object} location
 * @returns {string|null}
 */
export const shapeOf = (location) => (location?.physicalType?.coding || [])[0]?.code ?? null;

/**
 * The display name of a shape, for a chip or a row label.
 *
 * @param {string} shape
 * @returns {string|null}
 */
export const shapeTitle = (shape) => LOCATION_SHAPES.find((item) => item.value === shape)?.title ?? null;

/**
 * The icon a place is shown with, as the location tree shows it. A place whose shape is unset or
 * unknown gets the generic pin rather than nothing, so a row of icons never has a gap in it.
 *
 * @param {object} location
 * @returns {string} a FontAwesome name, drawn from the `far` set
 */
export const shapeIcon = (location) => LOCATION_SHAPES.find((item) => item.value === shapeOf(location))?.icon ?? 'location-dot';

/**
 * Builds the CodeableConcept array Location.type is stored as.
 *
 * @param {Array<string>} purposes
 * @returns {Array<object>|undefined} undefined for none, so the field is omitted rather than
 *   written as an empty array Aidbox would reject
 */
export const purposesToType = (purposes) => {
    const codes = (purposes || []).filter((code) => LOCATION_PURPOSES.some((purpose) => purpose.value === code));
    if (!codes.length) return undefined;
    return codes.map((code) => ({ coding: [{ system: PURPOSE_SYSTEM, code }] }));
};

/**
 * The shapes that may be added under a parent.
 *
 * Returns only what is allowed rather than everything with the rest disabled — a list of greyed
 * rows asks the reader to work out a rule instead of telling them one.
 *
 * A parent whose own shape we cannot read is treated permissively: everything but a root. That is
 * the pre-backfill estate, and refusing to let somebody add to it would be worse than offering a
 * shape they have to think about.
 *
 * @param {object|null} parent the place being added under, or null at the top level
 * @returns {Array<object>} entries from LOCATION_SHAPES, in hierarchy order
 */
export const shapesAllowedUnder = (parent) => {
    if (!parent) return LOCATION_SHAPES.filter((shape) => shape.value === ROOT_SHAPE);

    const parentShape = shapeOf(parent);
    const allowed = parentShape ? ALLOWED_CHILDREN[parentShape] : null;

    return LOCATION_SHAPES.filter((shape) => {
        if (shape.value === ROOT_SHAPE) return false;
        // A bed goes only where a patient can actually be assigned, whatever the shape rules say.
        if (shape.value === BED_SHAPE && !hasPurpose(parent, BED_REQUIRES_PURPOSE)) return false;
        return allowed ? allowed.includes(shape.value) : true;
    });
};

/**
 * Whether a shape may carry purposes at all. Only a bed may not — its shape already says what
 * it is for.
 *
 * @param {string} shape
 * @returns {boolean}
 */
export const shapeTakesPurposes = (shape) => shape !== BED_SHAPE;

/**
 * Order siblings the way the estate reads: outermost kind first, then by name.
 *
 * LOCATION_SHAPES is declared in containment order, so its index is the rank — a building sorts
 * above the wings inside it, and two wings sort against each other by name. An untyped place sorts
 * last rather than first, so backfill gaps surface at the bottom instead of displacing real rows.
 *
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
export const compareLocations = (a, b) => {
    const rank = (location) => {
        const index = LOCATION_SHAPES.findIndex((shape) => shape.value === shapeOf(location));
        return index === -1 ? LOCATION_SHAPES.length : index;
    };
    return rank(a) - rank(b) || (a?.name || '').localeCompare(b?.name || '');
};

/**
 * A place's ancestors, outermost first, followed by the place itself.
 *
 * This is what disambiguates two windows both called Window, so every picker labels with it
 * rather than with the bare name.
 *
 * @param {string} id
 * @param {Map<string, object>} byId
 * @returns {Array<object>}
 */
export const locationPath = (id, byId) => {
    const path = [];
    const seen = new Set();
    let current = byId?.get(id);
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        path.unshift(current);
        current = byId.get(current.partOf?.id);
    }
    return path;
};

/**
 * A place and everything under it, the place itself first.
 *
 * @param {string} id
 * @param {{byId: Map<string, object>, childrenOf: Map<string, Array<object>>}} index from buildIndex
 * @returns {Array<object>}
 */
export const subtree = (id, index) => {
    const found = [];
    const seen = new Set();
    const walk = (currentId) => {
        if (!currentId || seen.has(currentId)) return;
        seen.add(currentId);
        const location = index?.byId?.get(currentId);
        if (!location) return;
        found.push(location);
        (index?.childrenOf?.get(currentId) || []).forEach((child) => walk(child.id));
    };
    walk(id);
    return found;
};

/**
 * Several places and everything under each, once apiece.
 *
 * Users are granted top-level locations, so each id normally reaches a separate tree; a place
 * reached from a second id is still not repeated. An id the index does not hold reaches nothing.
 *
 * @param {Iterable<string>} ids
 * @param {{byId: Map<string, object>, childrenOf: Map<string, Array<object>>}} index from buildIndex
 * @returns {Array<object>} in first-reached order
 */
export const subtreesOf = (ids, index) => {
    const found = new Map();
    for (const id of ids || []) {
        subtree(id, index).forEach((location) => {
            if (!found.has(location.id)) found.set(location.id, location);
        });
    }
    return [...found.values()];
};

/**
 * The zone a place runs on: the nearest one declared at or above it.
 *
 * Only roots carry a timeZone, so reading the field off a room yields undefined and every
 * consumer of that silently falls back to the organization's.
 *
 * @param {string} id
 * @param {Map<string, object>} byId
 * @returns {string|undefined}
 */
export const zoneFor = (id, byId) => {
    const ancestors = locationPath(id, byId);
    for (let i = ancestors.length - 1; i >= 0; i--) {
        if (ancestors[i]?.timeZone) return ancestors[i].timeZone;
    }
    return undefined;
};

/**
 * A place named by its whole path, because two wings can each hold a Room 1.
 *
 * @param {string} id
 * @param {Map<string, object>} byId
 * @param {{fromDepth?: number}} [options] fromDepth drops leading steps; 1 omits the root
 * @returns {string}
 */
export const pathLabel = (id, byId, { fromDepth = 0 } = {}) => {
    const path = locationPath(id, byId);
    return (
        path
            .slice(fromDepth)
            .map((step) => step.name)
            .filter(Boolean)
            .join(' › ') ||
        byId?.get(id)?.name ||
        'Unnamed location'
    );
};
